/**
 * AISuggestButton — Phase 5
 *
 * Reusable inline "Suggest with AI" button that triggers a draft request
 * via POST /api/ai-copilot/draft. The caller passes a payload builder
 * function plus a feature key, and receives the parsed draft via onDraft.
 * Daily-cap (HTTP 429) and provider errors (HTTP 502/503) are surfaced to
 * the user via a tooltip-style toast through `sonner`.
 *
 * The button is responsible for ASKING the AI. It is NOT responsible for
 * persisting anything. Pair it with `AIDraftReviewDialog` for the human
 * review flow before any save.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function useAICopilotEnabled() {
  // Reads the same localStorage cache populated by AuthContext.syncFeatureFlags.
  try {
    const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
    return !!flags.care_plan_ai_assist_enabled;
  } catch { return false; }
}

export function AISuggestButton({
  feature, buildPayload, onDraft, label = 'Suggest with AI',
  size = 'sm', variant = 'outline', disabled = false, testid,
}) {
  const enabled = useAICopilotEnabled();
  const [loading, setLoading] = useState(false);
  const [statusInfo, setStatusInfo] = useState(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api.get('/ai-copilot/status')
      .then(r => { if (!cancelled) setStatusInfo(r.data.data); })
      .catch(() => { /* ignore — flag may be flipping */ });
    return () => { cancelled = true; };
  }, [enabled]);

  if (!enabled) return null;

  const click = async () => {
    setLoading(true);
    try {
      const payload = await buildPayload();
      if (!payload) { setLoading(false); return; }
      const r = await api.post('/ai-copilot/draft', { feature, ...payload });
      onDraft?.(r.data.data);
      if (statusInfo) {
        // best-effort refresh; non-blocking
        api.get('/ai-copilot/status')
          .then(rr => setStatusInfo(rr.data.data))
          .catch(() => {});
      }
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data?.error?.message;
      if (code === 429) toast.error(detail || 'Daily AI call cap reached.');
      else if (code === 503) toast.error(detail || 'AI provider not configured.');
      else if (code === 404) toast.error('AI Copilot is currently disabled.');
      else toast.error(detail || 'AI draft failed. Please try again.');
    } finally { setLoading(false); }
  };

  const remainingHint = statusInfo
    ? `${statusInfo.daily_remaining}/${statusInfo.daily_cap} AI calls left today`
    : '';

  return (
    <Button
      type="button" size={size} variant={variant}
      onClick={click} disabled={loading || disabled}
      title={remainingHint}
      data-testid={testid || `ai-suggest-${feature}`}
      className="gap-1.5"
    >
      {loading
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : <Sparkles className="h-3.5 w-3.5 text-amber-500" />}
      <span>{loading ? 'Drafting…' : label}</span>
    </Button>
  );
}
